/* eslint-disable no-console, class-methods-use-this, id-length */
import debounce from '../utils/debounce';
import {
  disableBodyScroll,
  enableBodyScroll,
} from 'body-scroll-lock/lib/bodyScrollLock.min.js';

function isDescendantOfAny(elements, child) { // eslint-disable-line func-style
  for (const element of elements) {
    let node = child.parentNode;

    while (node !== null) {
      if (node === element) {
        return true;
      }
      node = node.parentNode;
    }
  }

  return false;
}

export default class Search {
  constructor() {
    this.selector = '.c-search';

    this.selectors = {
      form: '.c-search__form',
      input: '.c-search__input',
      resultsContainer: '.c-search__results-container',
      close: '.c-search__close',
      toggle: '.header__search-toggle',
      toggleMobile: '.header__search-toggle--mobile',
      header: '.header',
      headerLoginMobile: '.header__login--mobile',
      noTransition: 'no-transition',
    };

    this.classNames = {
      searchBarActive: '--search-bar-active',
    };

    this.nodes = {};
    this.isExpanded = false;

    this.postTypeLabels = window.wp_ajax.post_type_labels || {
      post: 'Posts',
      page: 'Pages',
      attachment: 'Media',
      airfleet_style_guide: 'Style Guide', // eslint-disable-line camelcase
      'af-resource': 'Resources',
      'af-news-and-events': 'News and Events',
      'af-product': 'Products',
      'af-application': 'Applications',
    };
  }

  bootstrap() {
    const component = document.querySelector(this.selector);

    if (!component) {
      return;
    }

    this.getNodes({ component });

    this.nodes.input.addEventListener('input', debounce(this.fetchSearch.bind(this), 200));

    this.nodes.toggle.addEventListener('click', event => {
      event.preventDefault();
      document.body.classList.toggle(this.classNames.searchBarActive);
    });

    this.nodes.toggleMobile.addEventListener('click', event => {
      event.preventDefault();
      document.body.classList.toggle(this.classNames.searchBarActive);
    });

    this.nodes.headerLoginMobile.addEventListener('click', () => {
      document.body.classList.remove(this.classNames.searchBarActive);
    });

    this.nodes.close.addEventListener('click', event => {
      event.preventDefault();
      if (window.innerWidth < 992) {
        component.style.zIndex = '1000';
      }
      this.closeBar();
      document.body.classList.remove(this.classNames.searchBarActive);
      if (window.innerWidth < 992) {
        setTimeout(() => {
          component.classList.add(this.selectors.noTransition);
        }, 300);
      }
    });

    this.handleOutsideClick([ this.nodes.component, this.nodes.header ], () => {
      this.closeBar();
      document.body.classList.remove(this.classNames.searchBarActive);
    });

    component.addEventListener('transitionend', () => {
      if (window.innerWidth < 992) {
        if (!component.classList.contains(this.selectors.noTransition)) {
          component.style.zIndex = '1001';
          component.classList.add(this.selectors.noTransition);
        }

        setTimeout(() => {
          component.classList.remove(this.selectors.noTransition);
        }, 300);
      }
    });
  }

  handleOutsideClick(excludedElements, callback) {
    document.addEventListener('click', event => {
      if (!document.body.classList.contains(this.classNames.searchBarActive)) {
        return;
      }

      // Check if the clicked element is not within any of the excluded elements
      if (![ ...excludedElements ].some(element => event.target === element || isDescendantOfAny([ element ], event.target))) {
        // Call the provided callback function when a click occurs outside the excluded elements
        callback(); // eslint-disable-line callback-return
      }
    });
  }

  fetchSearch(event) { // eslint-disable-line class-methods-use-this
    const input = event.target;
    const query = input.value;
    const ajaxurl = window.wp_ajax.url || '/wp-admin/admin-ajax.php';

    if (query.length >= 3) {
      this.setExpanded(true);

      const formData = new FormData();

      formData.append('action', 'custom_search');
      formData.append('q', query);

      fetch(ajaxurl, {
        method: 'POST',
        body: formData,
      })
        .then(response => response.json())
        .then(data => {
          console.log(data);

          // Render items.
          this.renderItems(data);
        })
        .catch(error => {
          console.error('Error fetching data:', error);
        });
    } else {
      this.setExpanded(false);
    }
  }

  renderItems(data) {
    this.nodes.resultsContainer.innerHTML = '';

    if (data.length === 0) {
      const notFoundText = document.createElement('p');

      notFoundText.classList.add('c-search__notfound');
      notFoundText.innerHTML = 'No items found..';
      this.nodes.resultsContainer.append(notFoundText);

      return;
    }

    Object.entries(data).forEach(([ key, value ]) => {
      const postTypeTitle = document.createElement('p');

      postTypeTitle.classList.add('c-text--p1');
      postTypeTitle.innerHTML = this.postTypeLabels[key];

      this.nodes.resultsContainer.append(postTypeTitle);
      this.nodes.resultsContainer.append(this.createList(value, key));
    });
  }

  closeBar() {
    this.setExpanded(false);
    this.nodes.input.value = '';
  }

  createList(data, parentId) {
    const ul = document.createElement('ul');

    ul.id = parentId;
    ul.classList.add('c-text--p3');

    data.forEach(item => {
      const li = document.createElement('li');
      const a = document.createElement('a');

      a.href = item.link;
      a.textContent = this.decodeHtmlEntities(item.title);
      li.appendChild(a);
      ul.appendChild(li);
    });

    return ul;
  }

  setExpanded(expanded) {
    this.isExpanded = expanded;

    if (expanded) {
      document.body.classList.add('--search-bar-expanded');
      this.nodes.component.classList.add('c-search--expanded');
      disableBodyScroll(this.nodes.header);
    } else {
      document.body.classList.remove('--search-bar-expanded');
      this.nodes.component.classList.remove('c-search--expanded');
      enableBodyScroll(this.nodes.header);
    }
  }

  getNodes({ component }) {
    this.nodes = {
      component,
      form: component.querySelector(this.selectors.form),
      input: component.querySelector(this.selectors.input),
      resultsContainer: component.querySelector(this.selectors.resultsContainer),
      toggle: document.querySelector(this.selectors.toggle),
      toggleMobile: document.querySelector(this.selectors.toggleMobile),
      header: document.querySelector(this.selectors.header),
      close: component.querySelector(this.selectors.close),
      headerLoginMobile: document.querySelector(this.selectors.headerLoginMobile),
    };
  }

  decodeHtmlEntities(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    return doc.documentElement.textContent;
  }
}
